import { useAuditStatistics, useChangeRequests } from '../../hooks/useAdminApi';
import { NavLink } from 'react-router-dom';
import { BASE_APP_PAth } from '../../api/launchpad-api';

const ADMIN_BASE = `${BASE_APP_PAth}/admin`;

const QUICK_LINKS = [
    { path: '/appetite-mapping', icon: 'icon-commercial',    label: 'Appetite Mapping', desc: 'View and edit carrier appetite weights for Classic and Universal product lines.' },
    { path: '/approval-queue',   icon: 'icon-policy-review', label: 'Approval Queue',   desc: 'Review, approve, reject, and apply change requests in two gates.' },
    { path: '/bulk-operations',  icon: 'icon-documents',     label: 'Bulk Upload',      desc: 'Download a setup template for a carrier and line of business that have no appetite yet.' },
    { path: '/audit-logs',       icon: 'icon-clock',         label: 'Change History',   desc: 'Full audit trail of all applied data changes with before/after values.' },
];

export const AdminDashboard = () => {
    const { data: stats, isLoading: statsLoading } = useAuditStatistics();
    const { data: pendingCRs, isLoading: pendingLoading } = useChangeRequests('pending');
    const { data: reviewedCRs, isLoading: reviewedLoading } = useChangeRequests('reviewed');

    if (statsLoading || pendingLoading || reviewedLoading) {
        return (
            <div className="admin-empty">
                <span className="admin-spin admin-spin-lg" />
                <p>Loading dashboard…</p>
            </div>
        );
    }

    const pendingCount = pendingCRs?.totalCount ?? 0;
    const reviewedCount = reviewedCRs?.totalCount ?? 0;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h1>Overview</h1>
                    <p className="admin-page-subtitle">Monitor carrier appetite data and manage change requests across all products.</p>
                </div>
            </div>

            {(pendingCount > 0 || reviewedCount > 0) && (
                <div className="admin-alert admin-alert-info">
                    <span className="admin-icon icon-info" aria-hidden="true" />
                    <div className="admin-alert-body">
                        {pendingCount > 0 && (
                            <strong>{pendingCount} change request{pendingCount !== 1 ? 's' : ''} awaiting review. </strong>
                        )}
                        {reviewedCount > 0 && (
                            <strong>{reviewedCount} change request{reviewedCount !== 1 ? 's' : ''} awaiting approval. </strong>
                        )}
                        <NavLink to={`${ADMIN_BASE}/approval-queue`}>Go to Approval Queue</NavLink>
                    </div>
                </div>
            )}

            <div className="admin-stats">
                <StatCard title="Total Changes Applied" value={stats?.totalChanges ?? 0} />
                <StatCard title="Changes This Month"    value={stats?.changesThisMonth ?? 0} />
                <StatCard title="Awaiting Review"       value={pendingCount} highlight={pendingCount > 0} />
                <StatCard title="Awaiting Approval"     value={reviewedCount} highlight={reviewedCount > 0} />
                <StatCard title="Active Contributors"   value={stats?.activeUsers ?? 0} />
            </div>

            <h2 className="admin-section-title">Quick Access</h2>
            <div className="admin-quick-grid">
                {QUICK_LINKS.map(link => (
                    <NavLink key={link.path} className="admin-quick-tile" to={`${ADMIN_BASE}${link.path}`}>
                        <span className={`admin-icon admin-quick-tile-icon ${link.icon}`} aria-hidden="true" />
                        <span className="admin-quick-tile-body">
                            <span className="admin-quick-tile-label">{link.label}</span>
                            <span className="admin-quick-tile-desc">{link.desc}</span>
                        </span>
                    </NavLink>
                ))}
            </div>
        </div>
    );
};

function StatCard({ title, value, highlight }: {
    title: string;
    value: number;
    highlight?: boolean;
}) {
    return (
        <div className={`admin-stat${highlight ? ' admin-stat-warn' : ''}`}>
            <div className="admin-stat-label">{title}</div>
            <div className="admin-stat-value">{value.toLocaleString()}</div>
        </div>
    );
}
